import React, { useEffect, useRef, useState } from "react";
import { Eraser, Pen } from "lucide-react";

/**
 * PowerBuilt signature pad — touch + mouse, retina-crisp, no external deps.
 *
 * Props:
 *  - onChange(dataUrl | null): fires after every stroke (or after clear).
 *  - height (default 180)
 *  - disabled: renders a static, non-drawable canvas.
 */
export default function SignaturePad({ onChange, height = 180, disabled = false }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  // Retina-crisp sizing that survives resize.
  useEffect(() => {
    const cvs = canvasRef.current;
    const wrap = wrapRef.current;
    if (!cvs || !wrap) return;

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      // Preserve existing pixels so a resize doesn't wipe the signature.
      const prev = document.createElement("canvas");
      prev.width = cvs.width;
      prev.height = cvs.height;
      prev.getContext("2d").drawImage(cvs, 0, 0);

      cvs.width = Math.floor(rect.width * dpr);
      cvs.height = Math.floor(height * dpr);
      cvs.style.width = `${rect.width}px`;
      cvs.style.height = `${height}px`;

      const ctx = cvs.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, rect.width, height);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.2;
      ctx.strokeStyle = "#0A2540";

      // Redraw previous ink into new sized canvas (scaled).
      if (prev.width && prev.height) {
        ctx.drawImage(prev, 0, 0, rect.width, height);
      }
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [height]);

  const pointFrom = (evt) => {
    const cvs = canvasRef.current;
    const rect = cvs.getBoundingClientRect();
    const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
    const clientY = evt.touches ? evt.touches[0].clientY : evt.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (evt) => {
    if (disabled) return;
    evt.preventDefault();
    drawing.current = true;
    lastPt.current = pointFrom(evt);
  };

  const move = (evt) => {
    if (!drawing.current || disabled) return;
    evt.preventDefault();
    const cvs = canvasRef.current;
    const ctx = cvs.getContext("2d");
    const pt = pointFrom(evt);
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPt.current = pt;
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    lastPt.current = null;
    if (onChange && canvasRef.current) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    const rect = wrapRef.current.getBoundingClientRect();
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, cvs.width, cvs.height);
    ctx.restore();
    setHasInk(false);
    if (onChange) onChange(null);
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <canvas
        ref={canvasRef}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        data-testid="signature-pad-canvas"
        className={`w-full rounded-lg border-2 border-dashed border-[#C9A356]/50 bg-white touch-none select-none ${
          disabled ? "opacity-70 cursor-not-allowed" : "cursor-crosshair"
        }`}
        style={{ height }}
      />
      {!hasInk && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[#94A3B8] gap-2 text-sm">
          <Pen className="w-4 h-4" /> Sign with your finger, stylus or mouse
        </div>
      )}
      {!disabled && (
        <button
          type="button"
          onClick={clear}
          data-testid="signature-pad-clear"
          className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#475569] hover:text-[#0A2540] bg-white/80 px-2 py-1 rounded"
        >
          <Eraser className="w-3 h-3" /> Clear
        </button>
      )}
    </div>
  );
}
